import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FileText, Plus, Pencil, Eye, Trash2, Type, Calendar, Building2, Clock, Send, Users, UserCheck, UserX, Archive } from "lucide-react";
import { RelationBuilder } from "@/components/office/RelationBuilder";
import { RelationRenderer } from "@/components/office/RelationRenderer";
import { RelationsMonitor } from "@/components/office/RelationsMonitor";

function fmtDate(s) {
  if (!s) return "—";
  const d = new Date(s.length <= 10 ? `${s}T00:00:00` : s);
  return isNaN(d) ? s : d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
}

export function RelationsManager({ onActionChange }) {
  const [view, setView] = useState("list"); // list | builder | monitor
  const [editingId, setEditingId] = useState(null);
  const [monitorId, setMonitorId] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [renameItem, setRenameItem] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [delItem, setDelItem] = useState(null);
  const [pubSummary, setPubSummary] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [archiveItem, setArchiveItem] = useState(null);
  const [archiveInfo, setArchiveInfo] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/office/relations/templates").then((r) => setItems(r.data || [])).catch(() => setItems([])).finally(() => setLoading(false));
    onActionChange && onActionChange();
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditingId(null); setView("builder"); };
  const openEdit = (id) => { setEditingId(id); setView("builder"); };
  const backToList = () => { setView("list"); setEditingId(null); load(); };

  const openPreview = async (id) => {
    try { const r = await api.get(`/office/relations/templates/${id}`); setPreview(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doRename = async () => {
    if (!renameVal.trim()) return;
    try {
      await api.patch(`/office/relations/templates/${renameItem.id}/rename`, { title: renameVal.trim() });
      toast.success("Relazione rinominata"); setRenameItem(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doDelete = async () => {
    try {
      await api.delete(`/office/relations/templates/${delItem.id}`);
      toast.success("Bozza eliminata"); setDelItem(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openPublish = async (id) => {
    try { const r = await api.get(`/office/relations/templates/${id}/publish-summary`); setPubSummary(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doPublish = async () => {
    setPublishing(true);
    try {
      const r = await api.post(`/office/relations/templates/${pubSummary.id}/publish`);
      toast.success(`Relazione pubblicata · ${r.data.assigned} assegnate`);
      setPubSummary(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPublishing(false); }
  };
  const openArchive = async (it) => {
    try {
      const r = await api.get(`/office/relations/templates/${it.id}/archive-check`);
      setArchiveInfo(r.data); setArchiveItem(it);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const doArchive = async () => {
    try {
      await api.post(`/office/relations/templates/${archiveItem.id}/archive`);
      toast.success("Relazione archiviata"); setArchiveItem(null); setArchiveInfo(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (view === "builder") {
    return <RelationBuilder templateId={editingId} onClose={backToList} onSaved={backToList} />;
  }
  if (view === "monitor") {
    return <RelationsMonitor templateId={monitorId} onClose={() => { setView("list"); setMonitorId(null); load(); }} />;
  }

  return (
    <div className="space-y-4" data-testid="relations-manager">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-slate-500">Modelli di relazione salvati come bozza</p>
        <Button onClick={openNew} className="bg-[#6D5FB8] hover:bg-[#5B4A9E]" data-testid="relations-new-btn"><Plus className="h-4 w-4 mr-1.5" /> Nuova relazione</Button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500 p-4">Caricamento…</p>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center" data-testid="relations-manager-empty">
          <FileText className="h-8 w-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Nessuna relazione creata. Inizia con «Nuova relazione».</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((it) => {
            const published = it.stato === "pubblicata";
            return (
            <div key={it.id} className="rounded-xl border border-slate-200 bg-white shadow-sm p-4 flex flex-col" data-testid={`relation-card-${it.id}`}>
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-heading font-bold text-slate-900 leading-tight">{it.title}</h3>
                {published ? (
                  <span className="shrink-0 inline-flex items-center rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-xs font-semibold text-emerald-700" data-testid={`relation-status-${it.id}`}>Pubblicata</span>
                ) : (
                  <span className="shrink-0 inline-flex items-center rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-xs font-semibold text-amber-700" data-testid={`relation-status-${it.id}`}>Bozza</span>
                )}
              </div>
              <div className="mt-2 space-y-1 text-xs text-slate-500">
                <p className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" /> {it.appalto_nome} · {it.service_label}</p>
                {it.period_ref && <p className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {it.period_ref}</p>}
                <p className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> Scadenza: {fmtDate(it.due_date)}</p>
                <p className="flex items-center gap-1.5"><Type className="h-3.5 w-3.5" /> {it.fields_count} camp{it.fields_count === 1 ? "o" : "i"} · agg. {fmtDate(it.updated_at)}</p>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2">
                {published ? (
                  <>
                    <Button size="sm" variant="outline" onClick={() => { setMonitorId(it.id); setView("monitor"); }} data-testid={`relation-monitor-${it.id}`}>
                      <Eye className="h-3.5 w-3.5 mr-1" /> Monitora
                      {it.action_count > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[11px] font-bold">{it.action_count}</span>}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openArchive(it)} data-testid={`relation-archive-${it.id}`}><Archive className="h-3.5 w-3.5 mr-1" /> Archivia</Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="outline" onClick={() => openEdit(it.id)} data-testid={`relation-edit-${it.id}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Apri / Modifica</Button>
                    <Button size="sm" variant="outline" onClick={() => openPreview(it.id)} data-testid={`relation-preview-${it.id}`}><Eye className="h-3.5 w-3.5 mr-1" /> Anteprima</Button>
                    <Button size="sm" onClick={() => openPublish(it.id)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`relation-publish-${it.id}`}><Send className="h-3.5 w-3.5 mr-1" /> Pubblica</Button>
                    <button className="text-xs text-slate-500 hover:text-slate-800 px-2 py-1" onClick={() => { setRenameItem(it); setRenameVal(it.title); }} data-testid={`relation-rename-${it.id}`}>Rinomina</button>
                    <button className="ml-auto text-red-500 hover:text-red-600 p-1.5" title="Elimina" onClick={() => setDelItem(it)} data-testid={`relation-delete-${it.id}`}><Trash2 className="h-4 w-4" /></button>
                  </>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Anteprima */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="relation-preview-dialog">
          <DialogHeader><DialogTitle>Anteprima relazione</DialogTitle></DialogHeader>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4">
            {preview && <RelationRenderer title={preview.title} description={preview.description} fields={preview.fields || []} readOnly />}
          </div>
          <p className="text-xs text-slate-400 text-center">Anteprima visuale · non salva risposte</p>
        </DialogContent>
      </Dialog>

      {/* Rinomina */}
      <Dialog open={!!renameItem} onOpenChange={(o) => !o && setRenameItem(null)}>
        <DialogContent data-testid="relation-rename-dialog">
          <DialogHeader><DialogTitle>Rinomina relazione</DialogTitle></DialogHeader>
          <Input value={renameVal} onChange={(e) => setRenameVal(e.target.value)} placeholder="Titolo" data-testid="relation-rename-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameItem(null)}>Annulla</Button>
            <Button onClick={doRename} className="bg-[#6D5FB8] hover:bg-[#5B4A9E]" data-testid="relation-rename-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Elimina */}
      <AlertDialog open={!!delItem} onOpenChange={(o) => !o && setDelItem(null)}>
        <AlertDialogContent data-testid="relation-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la bozza?</AlertDialogTitle>
            <AlertDialogDescription>La relazione «{delItem?.title}» verrà eliminata definitivamente. L'operazione non è reversibile.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-600 hover:bg-red-700" data-testid="relation-delete-confirm">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Pubblica */}
      <Dialog open={!!pubSummary} onOpenChange={(o) => !o && setPubSummary(null)}>
        <DialogContent data-testid="relation-publish-dialog">
          <DialogHeader><DialogTitle>Pubblica relazione</DialogTitle></DialogHeader>
          {pubSummary && (
            <div className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-1 text-sm">
                <p className="font-semibold text-slate-900">{pubSummary.title}</p>
                <p className="text-xs text-slate-500">{pubSummary.appalto_nome} · {pubSummary.service_label}</p>
                {pubSummary.period_ref && <p className="text-xs text-slate-500">Periodo: {pubSummary.period_ref}</p>}
                <p className="text-xs text-slate-500">Scadenza: {fmtDate(pubSummary.due_date)}</p>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg border border-slate-200 p-3" data-testid="pub-total">
                  <Users className="h-4 w-4 mx-auto text-slate-400 mb-1" />
                  <p className="text-xl font-bold text-slate-900">{pubSummary.total_users}</p>
                  <p className="text-[11px] text-slate-500 leading-tight">Utenti totali</p>
                </div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="pub-assigned">
                  <UserCheck className="h-4 w-4 mx-auto text-emerald-500 mb-1" />
                  <p className="text-xl font-bold text-emerald-700">{pubSummary.assigned}</p>
                  <p className="text-[11px] text-emerald-600 leading-tight">Relazioni assegnate</p>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="pub-unassigned">
                  <UserX className="h-4 w-4 mx-auto text-amber-500 mb-1" />
                  <p className="text-xl font-bold text-amber-700">{pubSummary.unassigned}</p>
                  <p className="text-[11px] text-amber-600 leading-tight">Senza operatore titolare</p>
                </div>
              </div>
              {pubSummary.unassigned > 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="pub-warning">
                  Gli utenti senza operatore titolare non genereranno alcuna richiesta di compilazione a un lavoratore.
                </p>
              )}
              {pubSummary.can_publish ? (
                <p className="text-xs text-slate-500">Dopo la pubblicazione la relazione diventa immutabile: non potrà più essere modificata.</p>
              ) : (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="pub-blocked">
                  Nessun utente del servizio possiede un operatore titolare. Assegna prima almeno un operatore titolare nella scheda «Operatori titolari».
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPubSummary(null)}>Annulla</Button>
            <Button onClick={doPublish} disabled={publishing || !pubSummary?.can_publish} className="bg-emerald-600 hover:bg-emerald-700" data-testid="relation-publish-confirm">
              <Send className="h-4 w-4 mr-1.5" /> {publishing ? "Pubblicazione…" : "Pubblica"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archivia */}
      <AlertDialog open={!!archiveItem} onOpenChange={(o) => !o && (setArchiveItem(null), setArchiveInfo(null))}>
        <AlertDialogContent data-testid="relation-archive-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Archiviare la relazione?</AlertDialogTitle>
            <AlertDialogDescription>
              {archiveInfo && !archiveInfo.can_archive
                ? `La relazione «${archiveItem?.title}» contiene ancora ${archiveInfo.open_assigned} attività assegnate non approvate. Completa o approva tutte le compilazioni assegnate prima di archiviare.`
                : `La relazione «${archiveItem?.title}» verrà spostata nell'Archivio relazioni. I dati restano consultabili; le relazioni approvate non vengono modificate.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            {archiveInfo?.can_archive && (
              <AlertDialogAction onClick={doArchive} data-testid="relation-archive-confirm">Archivia</AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default RelationsManager;
